import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { EMAIL, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const defaultColSpan = {
	md: 12
};

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const SendEmailToClientForm = NiceModal.create(({ selectedRows, modalName }) => {
	const modal = useModal();
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();

	const schema = useMemo(
		() =>
			yup.object().shape({
				type: yup.string().nullable().required()
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			type: null
		}
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	const onSubmit = async (data) => {
		try {
			const response = await axios.post(EMAIL.SEND_ORDER_EMAIL_FROM_OPS, {
				type: data.type,
				data: selectedRows?.map((row) => ({
					orderId: row.ID,
					companyId: row.companyId
				}))
			});
			if (response.status === 200) {
				showSuccess();
				handleCloseModal();
			} else {
				throw new Error('Failed to send email');
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<Modal maxWidth="x-small" fullwidth name={modalName} className="modal-PaymentSlipModal">
			<ModalTitle>
				<Typography variant="p1">sendEmailToClient</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="emailType" isLoading={isLoading}>
						<EnumSelect
							name="type"
							enumName={ENUMS.EMAIL_TO_CLIENT_TYPES}
							value={watch('type')}
							setValue={(value) => setValue('type', value, validateOptions)}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />

				<Button
					label="send"
					variant="blue"
					loading={isLoading}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default SendEmailToClientForm;
